if (!customElements.get("cover-video")) {
  // How long the video has to stay near the viewport before it loads, so
  // scrolling straight past it never starts a download.
  const LOAD_DWELL = 250;

  const loadYouTubeApi = () => {
    if (window.coverVideoYouTubeApi) return window.coverVideoYouTubeApi;

    window.coverVideoYouTubeApi = new Promise((resolve, reject) => {
      if (window.YT && window.YT.Player) {
        resolve(window.YT);
        return;
      }

      if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        document.head.appendChild(tag);
      }

      // The api script (which another component may have injected) defines
      // YT.ready once it executes, so wait for it to appear.
      let attempts = 0;
      const check = () => {
        if (window.YT && window.YT.ready) {
          window.YT.ready(() => resolve(window.YT));
          return;
        }
        if ((attempts += 1) > 100) {
          reject(new Error("YouTube iframe api unavailable"));
          return;
        }
        setTimeout(check, 100);
      };
      check();
    });

    return window.coverVideoYouTubeApi;
  };

  const loadVimeoApi = () => {
    if (window.coverVideoVimeoApi) return window.coverVideoVimeoApi;

    window.coverVideoVimeoApi = new Promise((resolve, reject) => {
      if (window.Vimeo && window.Vimeo.Player) {
        resolve(window.Vimeo);
        return;
      }

      let tag = document.querySelector('script[src*="player.vimeo.com/api/player.js"]');
      if (!tag) {
        tag = document.createElement("script");
        tag.src = "https://player.vimeo.com/api/player.js";
        document.head.appendChild(tag);
      }

      tag.addEventListener("load", () => resolve(window.Vimeo), { once: true });
      tag.addEventListener("error", () => reject(new Error("Vimeo api unavailable")), { once: true });

      // Covers the case where the script had already finished loading.
      if (window.Vimeo && window.Vimeo.Player) resolve(window.Vimeo);
    });

    return window.coverVideoVimeoApi;
  };

  customElements.define(
    "cover-video",
    class CoverVideo extends HTMLElement {
      connectedCallback() {
        if (!this.initialised) this.setup();

        // Observers are rebuilt on every connect: a carousel or the theme
        // editor moving this element detaches it, and it has to keep working.
        this.startObserving();
      }

      setup() {
        this.initialised = true;

        this.frame = this.querySelector(".cover-video__frame");
        this.playButton = this.querySelector("[data-cover-video-play]");
        this.muteButton = this.querySelector("[data-cover-video-mute]");

        this.videoType = this.dataset.videoType;
        this.videoUrl = this.dataset.videoUrl || "";
        if (this.videoType === "embed") {
          this.videoType = this.detectPlatform(this.videoUrl);
        }
        this.videoId = this.dataset.videoId || this.extractVideoId(this.videoUrl);

        this.autoplay = this.dataset.autoplay !== "false";
        this.loop = this.dataset.loop !== "false";
        // Controls need a real player instance to talk to.
        this.useApi = this.playButton !== null || this.muteButton !== null;
        this.muted = true;
        this.inView = false;

        this.playerReady = new Promise((resolve) => {
          this.resolvePlayerReady = resolve;
        });

        this.classList.add("cover-video--js");
        this.bindControls();

        // Whether playback is wanted right now — flipped by the play/pause
        // control, and what scrolling back into view resumes from.
        this.playIntent = this.autoplay;
      }

      startObserving() {
        this.stopObserving();
        this.observeVisibility();

        if (this.loaded) return;

        if (this.dataset.eager === "true") {
          this.inView = true;
          this.load();
          return;
        }

        this.observeLoad();
      }

      disconnectedCallback() {
        this.stopObserving();

        // Being moved in the DOM disconnects and reconnects in the same frame,
        // so only tear the player down once it is really gone.
        requestAnimationFrame(() => {
          if (this.isConnected) return;
          if (this.player && typeof this.player.destroy === "function") {
            this.player.destroy();
            this.player = null;
          }
        });
      }

      observeLoad() {
        this.loadObserver = new IntersectionObserver(
          (entries) => {
            const approaching = entries.some((entry) => entry.isIntersecting);

            if (!approaching) {
              clearTimeout(this.loadTimer);
              this.loadTimer = null;
              return;
            }

            if (this.loadTimer) return;
            this.loadTimer = setTimeout(() => {
              this.loadTimer = null;
              this.load();
            }, LOAD_DWELL);
          },
          { rootMargin: "200px 0px" }
        );
        this.loadObserver.observe(this);
      }

      observeVisibility() {
        this.visibilityObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            this.inView = entry.isIntersecting;

            if (entry.isIntersecting) {
              this.onEnterView();
            } else {
              this.onLeaveView();
            }
          });
        });
        this.visibilityObserver.observe(this);
      }

      onEnterView() {
        if (!this.loaded || !this.playIntent || this.isPlaying) return;

        this.play();
      }

      onLeaveView() {
        // Not gated on isPlaying: playback may still be starting up, in which
        // case it has to be stopped as soon as it does.
        if (!this.loaded) return;

        this.pause();
      }

      stopObserving() {
        clearTimeout(this.loadTimer);
        this.loadTimer = null;

        [this.loadObserver, this.visibilityObserver].forEach((observer) => {
          if (observer) observer.disconnect();
        });
        this.loadObserver = null;
        this.visibilityObserver = null;
      }

      bindControls() {
        // Stop control clicks from following a link the video may be wrapped in.
        [this.playButton, this.muteButton].forEach((button) => {
          if (!button) return;
          button.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();
          });
        });

        if (this.playButton) {
          this.playButton.addEventListener("click", () => {
            this.playIntent = !this.isPlaying;

            if (this.isPlaying) {
              this.pause();
            } else {
              this.play();
            }
          });
        }

        if (this.muteButton) {
          this.muteButton.addEventListener("click", () => this.setMuted(!this.muted));
        }
      }

      detectPlatform(url) {
        if (/vimeo\.com/.test(url)) return "vimeo";
        if (/youtu\.?be/.test(url)) return "youtube";
        return "unknown";
      }

      extractVideoId(url) {
        if (this.videoType === "youtube") {
          const match = url.match(/(?:youtube\.com.*[?&]v=|youtu\.be\/|youtube\.com\/embed\/)([^"&?/ ]{11})/);
          return match ? match[1] : "";
        }

        if (this.videoType === "vimeo") {
          const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
          return match ? match[1] : "";
        }

        return "";
      }

      load() {
        if (this.loaded) return;
        this.loaded = true;

        if (this.loadObserver) {
          this.loadObserver.disconnect();
          this.loadObserver = null;
        }

        // Don't start something the shopper cannot see yet.
        this.playOnLoad = this.playIntent && this.inView;

        if (this.videoType === "file") {
          this.loadFile();
        } else if (this.videoType === "youtube" && this.videoId) {
          this.loadYouTube();
        } else if (this.videoType === "vimeo" && this.videoId) {
          this.loadVimeo();
        } else {
          this.loaded = false;
        }
      }

      reveal() {
        this.classList.add("cover-video--ready");
      }

      setPlaying(isPlaying) {
        this.isPlaying = isPlaying;
        this.classList.toggle("cover-video--playing", isPlaying);
      }

      loadFile() {
        this.video = this.frame.querySelector("video");
        if (!this.video) {
          this.loaded = false;
          return;
        }

        this.video.addEventListener("loadeddata", () => this.reveal());
        this.video.addEventListener("playing", () => {
          this.reveal();
          this.setPlaying(true);
          if (!this.inView) this.pause();
        });
        this.video.addEventListener("pause", () => this.setPlaying(false));
        this.video.addEventListener("ended", () => this.setPlaying(false));

        this.video.muted = this.muted;
        this.resolvePlayerReady();

        if (this.playOnLoad) {
          this.video.preload = "auto";
          this.play();
        }
      }

      async loadYouTube() {
        let api;
        try {
          api = await loadYouTubeApi();
        } catch (error) {
          return;
        }

        if (!this.isConnected) return;

        const target = document.createElement("div");
        this.frame.appendChild(target);

        this.player = new api.Player(target, {
          videoId: this.videoId,
          playerVars: {
            autoplay: this.playOnLoad ? 1 : 0,
            mute: 1,
            controls: 0,
            loop: this.loop ? 1 : 0,
            playlist: this.videoId,
            playsinline: 1,
            modestbranding: 1,
            rel: 0,
            disablekb: 1,
            fs: 0,
            iv_load_policy: 3,
          },
          events: {
            onReady: (event) => {
              if (this.muted) {
                event.target.mute();
              } else {
                event.target.unMute();
              }
              this.resolvePlayerReady();

              const iframe = this.frame.querySelector("iframe");
              if (iframe) {
                iframe.setAttribute("tabindex", "-1");
                iframe.setAttribute("aria-hidden", "true");
              }

              // Re-check: the player takes a moment to build, and the shopper
              // may have scrolled on by then.
              if (this.playIntent && this.inView) {
                event.target.playVideo();
              } else {
                event.target.pauseVideo();
              }
            },
            onStateChange: (event) => {
              if (event.data === api.PlayerState.PLAYING) {
                this.reveal();
                this.setPlaying(true);
                if (!this.inView) this.pause();
                return;
              }

              if (event.data === api.PlayerState.PAUSED) {
                this.setPlaying(false);
                return;
              }

              if (event.data === api.PlayerState.ENDED) {
                if (this.loop) {
                  event.target.seekTo(0);
                  event.target.playVideo();
                } else {
                  this.setPlaying(false);
                }
              }
            },
          },
        });
      }

      async loadVimeo() {
        const iframe = document.createElement("iframe");
        const params = [
          `autoplay=${this.playOnLoad ? 1 : 0}`,
          "muted=1",
          `loop=${this.loop ? 1 : 0}`,
          "autopause=0",
          // background mode is chromeless, but it also blocks playback control,
          // so it is only used when there are no controls to honour.
          this.useApi ? "controls=0" : "background=1",
        ];
        iframe.src = `https://player.vimeo.com/video/${this.videoId}?${params.join("&")}`;
        iframe.title = "Vimeo video player";
        iframe.setAttribute("frameborder", "0");
        iframe.setAttribute("allow", "autoplay; fullscreen; picture-in-picture");
        iframe.setAttribute("tabindex", "-1");
        iframe.setAttribute("aria-hidden", "true");
        this.frame.appendChild(iframe);

        if (!this.useApi) {
          iframe.addEventListener("load", () => setTimeout(() => this.reveal(), 300), { once: true });
          return;
        }

        let api;
        try {
          api = await loadVimeoApi();
        } catch (error) {
          iframe.addEventListener("load", () => setTimeout(() => this.reveal(), 300), { once: true });
          return;
        }

        if (!this.isConnected) return;

        this.player = new api.Player(iframe);
        this.player.setVolume(0);
        this.player.on("play", () => {
          this.reveal();
          this.setPlaying(true);
          if (!this.inView) this.pause();
        });
        this.player.on("pause", () => this.setPlaying(false));
        this.player.on("ended", () => this.setPlaying(false));

        this.resolvePlayerReady();

        if (this.playIntent && this.inView) {
          this.play();
        }
      }

      async play() {
        if (!this.loaded) this.load();
        await this.playerReady;

        // A player can take a while to become ready — by then the shopper may
        // have scrolled on, or paused it.
        if (!this.playIntent || !this.inView) return;

        if (this.videoType === "file") {
          if (!this.video) return;
          const playback = this.video.play();
          if (playback && typeof playback.catch === "function") {
            playback.catch(() => this.reveal());
          }
          return;
        }

        if (!this.player) return;

        if (this.videoType === "youtube") {
          this.player.playVideo();
        } else if (this.videoType === "vimeo") {
          this.player.play().catch(() => {});
        }
      }

      async pause() {
        await this.playerReady;

        if (this.videoType === "file") {
          if (this.video) this.video.pause();
          return;
        }

        if (!this.player) return;

        if (this.videoType === "youtube") {
          this.player.pauseVideo();
        } else if (this.videoType === "vimeo") {
          this.player.pause().catch(() => {});
        }
      }

      async setMuted(muted) {
        this.muted = muted;
        this.classList.toggle("cover-video--unmuted", !muted);

        await this.playerReady;

        if (this.videoType === "file") {
          if (this.video) this.video.muted = muted;
          return;
        }

        if (!this.player) return;

        if (this.videoType === "youtube") {
          if (muted) {
            this.player.mute();
          } else {
            this.player.unMute();
          }
        } else if (this.videoType === "vimeo") {
          if (typeof this.player.setMuted === "function") {
            this.player.setMuted(muted);
          } else {
            this.player.setVolume(muted ? 0 : 1);
          }
        }
      }
    }
  );
}
